import React, { useCallback, useEffect, useState } from 'react';

const LANGS = [
    { code: 'en', label: 'EN', flag: 'us', name: 'English' },
    { code: 'ar', label: 'AR', flag: 'sa', name: 'العربية' },
    { code: 'tr', label: 'TR', flag: 'tr', name: 'Türkçe' },
];

const SECTION_IDS = ['hero', 'about', 'plans', 'blog', 'contact'];

const Navbar = ({ t, i18n, handleLanguageChange }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [activeSection, setActiveSection] = useState('hero');
    const isRTL = i18n.language === 'ar';
    const isTR = i18n.language === 'tr';

    const closeMenu = useCallback(() => setIsMenuOpen(false), []);

    // Solidify navbar once the page scrolls
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Scroll-spy: highlight the section currently in view
    useEffect(() => {
        const sections = SECTION_IDS.map((id) => document.getElementById(id)).filter(Boolean);
        if (!sections.length) return undefined;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setActiveSection(entry.target.id);
                });
            },
            { rootMargin: '-45% 0px -45% 0px' }
        );
        sections.forEach((s) => observer.observe(s));
        return () => observer.disconnect();
    }, []);

    // Escape closes the mobile menu; lock body scroll while open
    useEffect(() => {
        if (!isMenuOpen) return undefined;
        const onKey = (e) => e.key === 'Escape' && closeMenu();
        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [isMenuOpen, closeMenu]);

    const navItems = [
        { href: '#hero', id: 'hero', label: t('nav-home') },
        { href: '#about', id: 'about', label: t('nav-about') },
        { href: '#blog', id: 'blog', label: t('nav-blog') },
        { href: '#contact', id: 'contact', label: t('nav-contact') },
    ];

    const linkSize = isTR ? 'md:text-[0.9rem]' : 'md:text-[0.95rem]';

    return (
        <nav
            aria-label="Main navigation"
            className={`fixed top-0 inset-x-0 z-[1000] transition-all duration-300 ${
                scrolled
                    ? 'bg-white/95 backdrop-blur-md shadow-[0_2px_20px_rgba(10,80,68,0.1)] border-b border-line'
                    : 'bg-mist/70 backdrop-blur-sm border-b border-transparent'
            }`}
        >
            <div className="sk-container flex items-center justify-between gap-3 h-[72px]">
                {/* Brand */}
                <a href="#hero" className="flex items-center gap-2.5 min-w-0" onClick={closeMenu}>
                    <img
                        src="/sk-clinic-logo.png"
                        alt=""
                        className={`rounded-full border-2 border-primary/20 bg-white p-1 transition-all duration-300 ${
                            scrolled ? 'h-10 w-10' : 'h-11 w-11'
                        }`}
                    />
                    <span className="min-w-0 leading-tight">
                        <span className="block font-heading font-bold text-primary-deep text-[1.15rem] truncate">
                            Sara Khedr
                        </span>
                        <span className="block text-[0.72rem] font-semibold tracking-wide text-ink-soft uppercase truncate">
                            {t('nav-subtitle')}
                        </span>
                    </span>
                </a>

                {/* Desktop links */}
                <ul className="hidden md:flex items-center gap-1 list-none m-0 p-0">
                    {navItems.map((item) => (
                        <li key={item.href}>
                            <a
                                href={item.href}
                                aria-current={activeSection === item.id ? 'true' : undefined}
                                className={`relative block px-3.5 py-2 rounded-full font-heading font-medium ${linkSize} whitespace-nowrap transition-colors duration-200 ${
                                    activeSection === item.id
                                        ? 'text-white bg-primary'
                                        : 'text-ink hover:text-primary hover:bg-primary-soft'
                                }`}
                            >
                                {item.label}
                            </a>
                        </li>
                    ))}
                </ul>

                {/* Right cluster: language + CTA + burger */}
                <div className="flex items-center gap-2.5">
                    {/* Language selector */}
                    <div
                        role="group"
                        aria-label="Language"
                        className="flex items-center gap-0.5 rounded-full border border-line bg-white/80 p-0.5"
                    >
                        {LANGS.map((lang) => (
                            <button
                                key={lang.code}
                                type="button"
                                onClick={() => handleLanguageChange(lang.code)}
                                aria-pressed={i18n.language === lang.code}
                                aria-label={lang.name}
                                className={`flex items-center gap-1 px-2 py-1.5 min-h-[36px] rounded-full text-[0.78rem] font-heading font-semibold cursor-pointer border-none transition-colors duration-200 ${
                                    i18n.language === lang.code
                                        ? 'bg-primary text-white'
                                        : 'bg-transparent text-ink-soft hover:bg-primary-soft hover:text-primary'
                                }`}
                            >
                                <img src={`https://flagcdn.com/w20/${lang.flag}.png`} alt="" className="w-4 h-auto rounded-[2px]" />
                                <span className="hidden sm:inline">{lang.label}</span>
                            </button>
                        ))}
                    </div>

                    {/* Desktop CTA */}
                    <a
                        href="#contact"
                        className={`max-lg:!hidden sk-btn sk-btn--primary !px-5 !py-2.5 !min-h-[44px] ${isTR ? '!text-[0.85rem]' : '!text-[0.9rem]'} whitespace-nowrap`}
                    >
                        {t('nav-book-now')}
                    </a>

                    {/* Burger */}
                    <button
                        type="button"
                        onClick={() => setIsMenuOpen((v) => !v)}
                        aria-expanded={isMenuOpen}
                        aria-controls="mobile-menu"
                        aria-label="Toggle menu"
                        className="md:hidden relative flex flex-col justify-center items-center gap-[5px] w-11 h-11 rounded-xl bg-transparent border border-line cursor-pointer z-[1002]"
                    >
                        <span className={`w-5 h-[2px] rounded bg-primary-deep transition-transform duration-300 ${isMenuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
                        <span className={`w-5 h-[2px] rounded bg-primary-deep transition-opacity duration-200 ${isMenuOpen ? 'opacity-0' : ''}`} />
                        <span className={`w-5 h-[2px] rounded bg-primary-deep transition-transform duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Mobile overlay */}
            <div
                aria-hidden="true"
                onClick={closeMenu}
                className={`md:hidden fixed inset-0 top-[72px] bg-primary-deep/40 backdrop-blur-[2px] transition-opacity duration-300 z-[1000] ${
                    isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
            />

            {/* Mobile sheet */}
            <div
                id="mobile-menu"
                className={`md:hidden fixed top-[72px] inset-x-3 rounded-2xl bg-white shadow-[0_24px_56px_rgba(10,80,68,0.18)] border border-line overflow-hidden transition-all duration-300 z-[1001] ${
                    isMenuOpen ? 'opacity-100 translate-y-2' : 'opacity-0 -translate-y-3 pointer-events-none'
                }`}
            >
                <ul className="list-none m-0 p-2">
                    {navItems.map((item, index) => (
                        <li key={item.href}>
                            <a
                                href={item.href}
                                onClick={closeMenu}
                                style={{ transitionDelay: isMenuOpen ? `${60 + index * 40}ms` : '0ms' }}
                                className={`flex items-center justify-between px-4 py-3.5 min-h-[52px] rounded-xl font-heading font-medium text-[1.02rem] transition-all duration-300 ${
                                    activeSection === item.id
                                        ? 'bg-primary-soft text-primary'
                                        : 'text-ink hover:bg-mist'
                                } ${isMenuOpen ? 'opacity-100 translate-x-0' : `opacity-0 ${isRTL ? 'translate-x-4' : '-translate-x-4'}`}`}
                            >
                                {item.label}
                                <i className={`fas fa-chevron-${isRTL ? 'left' : 'right'} text-[0.7rem] text-ink-soft`} aria-hidden="true" />
                            </a>
                        </li>
                    ))}
                    <li className="p-2 pt-3">
                        <a href="#contact" onClick={closeMenu} className="sk-btn sk-btn--primary w-full">
                            {t('nav-book-now')}
                        </a>
                    </li>
                </ul>
            </div>
        </nav>
    );
};

export default Navbar;
